import {
  type PermissionRegistryEntry,
  type PermissionRegistryGroup,
  type RoleDetail,
} from '@utazzo/shared';
import { UzButton, UzCheckbox, UzConfirmDialog } from '@utazzo/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { applyWithPrerequisites } from '../prerequisites';
import { useSetRolePermissions } from '../use-roles';

interface PermissionMatrixProps {
  role: RoleDetail;
  /// Flat module list. Each module carries its `groupKey`; we bin
  /// them by group in this component to render under collapsible
  /// section headers.
  registry: ReadonlyArray<PermissionRegistryEntry>;
  /// Group headers in display order (matches sidebar order).
  groups: ReadonlyArray<PermissionRegistryGroup>;
  /// Inherited permissions come from the parent chain. The matrix
  /// only persists direct grants on this role; inherited codes are
  /// not rendered as disabled/grey rows.
  inheritedSet: ReadonlySet<string>;
}

/**
 * Permission matrix organised by sidebar group (ADR-0056). Each
 * `groups` entry is a collapsible section; inside each section every
 * `registry` module whose `groupKey` matches renders as a row with
 * its action checkboxes.
 *
 * Per-group controls:
 *   - "Grant whole group" / "Revoke whole group" - flips every
 *     toggleable action in the section in one click.
 *   - section header is clickable to collapse / expand.
 *
 * Per-module controls (unchanged from v1):
 *   - "Grant all" / "Clear all" per module row.
 *   - individual action checkbox per code.
 *
 * Save is a single PUT with the resulting permission list; the diff
 * is reported in the success toast.
 */
/**
 * Group whose toggles ask for confirmation before they apply.
 *
 * 2026-08-04. System holds the codes that can hand out further access or
 * change how the platform runs - roles, users, secrets, settings, notification
 * rules. A mis-click there is materially different from one on a master-data
 * module, so every System toggle (single action, "Grant/Clear all" on a row,
 * and "Grant/Revoke group") routes through a confirm step. Every other group
 * toggles freely, so the dialog stays rare enough to actually be read.
 */
const CONFIRM_GROUP_ID = 'system';

/** A System toggle waiting on confirmation. */
type PendingChange =
  | { kind: 'code'; code: string; moduleLabel: string; action: string; granting: boolean }
  | { kind: 'module'; entry: PermissionRegistryEntry; allOn: boolean; count: number }
  | { kind: 'group'; groupId: string; groupLabel: string; allOn: boolean; count: number };

export const PermissionMatrix = ({
  role,
  registry,
  groups,
  inheritedSet,
}: PermissionMatrixProps): React.JSX.Element => {
  const setPerms = useSetRolePermissions();
  const initial = useMemo(() => new Set(role.permissions), [role.permissions]);
  const [selected, setSelected] = useState<Set<string>>(initial);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<PendingChange | null>(null);

  useEffect(() => {
    setSelected(new Set(role.permissions));
  }, [role.permissions]);

  const isDirty = useMemo(() => {
    if (selected.size !== initial.size) return true;
    for (const p of selected) if (!initial.has(p)) return true;
    return false;
  }, [selected, initial]);

  // Modules binned by groupKey - O(modules) once, reused on every render.
  const modulesByGroup = useMemo(() => {
    const map = new Map<string, PermissionRegistryEntry[]>();
    for (const m of registry) {
      const list = map.get(m.groupKey) ?? [];
      list.push(m);
      map.set(m.groupKey, list);
    }
    return map;
  }, [registry]);

  /**
   * Every change goes through here: a section / tab ticked brings its page
   * access along, page access unticked takes its sections with it (see
   * ../prerequisites). Codes the role inherits already satisfy a
   * prerequisite, so they are never added or used to remove anything.
   */
  const change = (edit: (next: Set<string>) => void): void => {
    const next = new Set(selected);
    edit(next);
    setSelected(applyWithPrerequisites(selected, next, inheritedSet, registry));
  };

  const toggle = (code: string): void => {
    change((next) => {
      if (next.has(code)) next.delete(code);
      else next.add(code);
    });
  };

  const toggleModule = (entry: PermissionRegistryEntry, allOn: boolean): void => {
    change((next) => {
      for (const action of entry.actions) {
        const code = `${entry.moduleKey}.${action}`;
        if (allOn) next.delete(code);
        else next.add(code);
      }
    });
  };

  const toggleGroup = (groupId: string, allOn: boolean): void => {
    change((next) => {
      const modules = modulesByGroup.get(groupId) ?? [];
      for (const m of modules) {
        for (const action of m.actions) {
          const code = `${m.moduleKey}.${action}`;
          if (inheritedSet.has(code) && !selected.has(code)) continue;
          if (allOn) next.delete(code);
          else next.add(code);
        }
      }
    });
  };

  /* ---------------------------------------------------------------- *
   * Confirmation gate. `request*` is what the UI calls; for every
   * group except System it applies immediately, exactly as before.
   * ---------------------------------------------------------------- */

  const requestToggle = (
    groupId: string,
    code: string,
    moduleLabel: string,
    action: string,
  ): void => {
    if (groupId !== CONFIRM_GROUP_ID) {
      toggle(code);
      return;
    }
    setPending({ kind: 'code', code, moduleLabel, action, granting: !selected.has(code) });
  };

  const requestToggleModule = (
    groupId: string,
    entry: PermissionRegistryEntry,
    allOn: boolean,
  ): void => {
    if (groupId !== CONFIRM_GROUP_ID) {
      toggleModule(entry, allOn);
      return;
    }
    setPending({ kind: 'module', entry, allOn, count: entry.actions.length });
  };

  const requestToggleGroup = (groupId: string, groupLabel: string, allOn: boolean): void => {
    if (groupId !== CONFIRM_GROUP_ID) {
      toggleGroup(groupId, allOn);
      return;
    }
    // Count what the flip would actually touch, so the dialog can state a
    // real number rather than "all of them".
    let count = 0;
    for (const m of modulesByGroup.get(groupId) ?? []) {
      for (const action of m.actions) {
        const code = `${m.moduleKey}.${action}`;
        if (inheritedSet.has(code) && !selected.has(code)) continue;
        if (allOn ? selected.has(code) : !selected.has(code)) count += 1;
      }
    }
    setPending({ kind: 'group', groupId, groupLabel, allOn, count });
  };

  const applyPending = (): void => {
    if (!pending) return;
    if (pending.kind === 'code') toggle(pending.code);
    else if (pending.kind === 'module') toggleModule(pending.entry, pending.allOn);
    else toggleGroup(pending.groupId, pending.allOn);
    setPending(null);
  };

  /** Dialog copy for the pending change. Grant and revoke read differently. */
  const pendingCopy = (): { title: string; description: string; confirmLabel: string } => {
    if (!pending) return { title: '', description: '', confirmLabel: 'Confirm' };
    if (pending.kind === 'code') {
      const verb = pending.granting ? 'Grant' : 'Revoke';
      return {
        title: `${verb} ${pending.moduleLabel} · ${pending.action}?`,
        description: pending.granting
          ? `"${role.name}" will be able to ${pending.action} ${pending.moduleLabel}. System permissions control access and platform configuration - grant only what this role needs. Nothing is saved until you press "Save permissions".`
          : `"${role.name}" will lose ${pending.action} on ${pending.moduleLabel}. Nothing is saved until you press "Save permissions".`,
        confirmLabel: verb,
      };
    }
    if (pending.kind === 'module') {
      const verb = pending.allOn ? 'Clear' : 'Grant';
      return {
        title: `${verb} all ${pending.count} actions on ${pending.entry.label}?`,
        description: `This flips every action on ${pending.entry.label} (${pending.entry.moduleKey}) for "${role.name}" at once. Nothing is saved until you press "Save permissions".`,
        confirmLabel: `${verb} all`,
      };
    }
    const verb = pending.allOn ? 'Revoke' : 'Grant';
    return {
      title: `${verb} the whole ${pending.groupLabel} group?`,
      description: `This changes ${pending.count} permission${pending.count === 1 ? '' : 's'} for "${role.name}" in one step - every System module at once, including roles, users and secrets. Nothing is saved until you press "Save permissions".`,
      confirmLabel: `${verb} group`,
    };
  };

  const toggleCollapse = (groupId: string): void => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  /// Compute the granted / revoked diff so the success toast can spell
  /// out the change ("Granted 12, revoked 4 on role Sales agent").
  const computeDiff = (): { granted: string[]; revoked: string[] } => {
    const granted: string[] = [];
    const revoked: string[] = [];
    for (const p of selected) if (!initial.has(p)) granted.push(p);
    for (const p of initial) if (!selected.has(p)) revoked.push(p);
    return { granted, revoked };
  };

  /// Module + group names involved in this diff. The toast spells
  /// them out so the operator doesn't have to compare matrices.
  const labelsForDiff = (codes: string[]): string => {
    const labels = new Set<string>();
    for (const code of codes) {
      // `<group>.<module>.<action>` -> moduleKey = `<group>.<module>`
      const lastDot = code.lastIndexOf('.');
      const moduleKey = lastDot > 0 ? code.slice(0, lastDot) : code;
      const entry = registry.find((m) => m.moduleKey === moduleKey);
      labels.add(entry?.label ?? moduleKey);
    }
    return Array.from(labels).join(', ');
  };

  const handleSave = async (): Promise<void> => {
    const diff = computeDiff();
    try {
      await setPerms.mutateAsync({
        id: role.id,
        payload: { permissions: Array.from(selected) },
      });
      const parts: string[] = [];
      if (diff.granted.length > 0) parts.push(`Granted ${diff.granted.length}`);
      if (diff.revoked.length > 0) parts.push(`revoked ${diff.revoked.length}`);
      const summary = parts.join(', ') || 'No changes';
      const modules = labelsForDiff([...diff.granted, ...diff.revoked]);
      toast.success(`${summary} on ${role.name}`, {
        description: modules ? `Modules: ${modules}` : undefined,
        duration: 5000,
      });
    } catch {
      // The mutation's onError already toasts the failure.
    }
  };

  const handleReset = (): void => {
    setSelected(new Set(initial));
  };

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-zinc-900">Permissions</h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Grouped by sidebar group. Check or uncheck each permission for this role. Saves as a
            single audited change.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <UzButton variant="ghost" onClick={handleReset} disabled={setPerms.isPending}>
              Reset
            </UzButton>
          )}
          <UzButton
            onClick={handleSave}
            loading={setPerms.isPending}
            disabled={!isDirty || setPerms.isPending}
          >
            Save permissions
          </UzButton>
        </div>
      </header>

      <div className="flex flex-col gap-3">
        {groups.map((group) => {
          const modules = modulesByGroup.get(group.id) ?? [];
          if (modules.length === 0) return null;
          const collapsed = collapsedGroups.has(group.id);

          // Group-level counters: how many of this group's codes are
          // selected vs inherited vs total. Drives the header label
          // and the "Grant whole group" button state.
          let groupTotal = 0;
          let groupOwn = 0;
          let groupInherited = 0;
          for (const m of modules) {
            for (const action of m.actions) {
              const code = `${m.moduleKey}.${action}`;
              groupTotal += 1;
              if (selected.has(code)) groupOwn += 1;
              else if (inheritedSet.has(code)) groupInherited += 1;
            }
          }
          const allOn = groupOwn === groupTotal && groupTotal > 0;

          return (
            <div
              key={group.id}
              className="overflow-hidden rounded-md border border-zinc-200 bg-white"
            >
              <button
                type="button"
                onClick={() => toggleCollapse(group.id)}
                className="flex w-full items-center justify-between gap-3 border-b border-zinc-100 bg-zinc-50/60 px-3 py-2 text-left hover:bg-zinc-50"
              >
                <div className="flex items-center gap-2">
                  {collapsed ? (
                    <ChevronRight className="h-4 w-4 text-zinc-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-zinc-500" />
                  )}
                  <div>
                    <div className="text-sm font-semibold text-zinc-900">{group.label}</div>
                    <div className="text-[11px] text-zinc-500">{group.description}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-zinc-600">
                  <span>
                    {groupOwn}/{groupTotal} own
                    {groupInherited > 0 ? ` · +${groupInherited} inh.` : ''}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      requestToggleGroup(group.id, group.label, allOn);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        requestToggleGroup(group.id, group.label, allOn);
                      }
                    }}
                    className="rounded border border-zinc-300 px-2 py-0.5 text-[11px] font-medium hover:bg-white"
                  >
                    {allOn ? 'Revoke group' : 'Grant group'}
                  </span>
                </div>
              </button>

              {!collapsed && (
                <div className="divide-y divide-zinc-100">
                  {modules.map((module) => {
                    const codes = module.actions.map((a) => `${module.moduleKey}.${a}`);
                    const ownCount = codes.filter((c) => selected.has(c)).length;
                    const allModuleOn = ownCount === codes.length && codes.length > 0;
                    const anyOn = ownCount > 0;
                    const inheritedCount = codes.filter(
                      (c) => inheritedSet.has(c) && !selected.has(c),
                    ).length;
                    return (
                      <div
                        key={module.moduleKey}
                        // Stacks below xl; the desktop 3-column matrix is
                        // unchanged (the fixed 220/140px rails need the width).
                        className="grid grid-cols-1 items-start gap-3 px-3 py-3 xl:grid-cols-[220px_1fr_140px] xl:gap-4"
                      >
                        <div>
                          <div className="text-sm font-medium text-zinc-900">{module.label}</div>
                          {module.description && (
                            <div className="mt-0.5 text-xs text-zinc-500">{module.description}</div>
                          )}
                          <div className="mt-1 text-[11px] tracking-wider text-zinc-400 uppercase">
                            {module.moduleKey}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {module.actions.map((action) => {
                            const code = `${module.moduleKey}.${action}`;
                            return (
                              <label
                                key={code}
                                className="flex items-center gap-2 text-sm text-zinc-700"
                              >
                                <UzCheckbox
                                  checked={selected.has(code)}
                                  disabled={setPerms.isPending}
                                  onCheckedChange={() =>
                                    requestToggle(group.id, code, module.label, action)
                                  }
                                />
                                <span className="font-mono text-xs">{action}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="flex flex-col items-end gap-1 text-xs">
                          <UzButton
                            variant="ghost"
                            onClick={() => requestToggleModule(group.id, module, allModuleOn)}
                            disabled={setPerms.isPending}
                          >
                            {allModuleOn ? 'Clear all' : 'Grant all'}
                          </UzButton>
                          <span className="text-zinc-500">
                            {ownCount}/{codes.length} own
                            {inheritedCount > 0 ? ` · +${inheritedCount} inh.` : ''}
                          </span>
                          {anyOn && (
                            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-emerald-700 uppercase">
                              granted
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <UzConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={pendingCopy().title}
        description={pendingCopy().description}
        confirmLabel={pendingCopy().confirmLabel}
        // Revokes fail closed, so only the grant direction is styled as the
        // dangerous one.
        tone={
          pending && (pending.kind === 'code' ? pending.granting : !pending.allOn)
            ? 'destructive'
            : 'primary'
        }
        onConfirm={applyPending}
      />
    </section>
  );
};
